import {
    ArrowLeft,
    Building2,
    Calendar,
    Edit,
    Eye,
    MapPin,
    Phone,
    Settings,
    Trash2,
    Users
} from 'lucide-react';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const DetailField = ({ label, value, className = '' }) => (
  <div className={`rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 md:px-4 ${className}`}>
    <dt className="text-xs font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm font-semibold text-gray-900 break-words">{value || '-'}</dd>
  </div>
);

const SummaryCard = ({ icon: Icon, label, value, tone = 'blue' }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-50 text-slate-600',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm md:p-4">
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg md:h-10 md:w-10 ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500">{label}</p>
          <p className="mt-1 truncate text-base font-bold text-gray-900 md:text-lg">{value || '-'}</p>
        </div>
      </div>
    </div>
  );
};

const RMCBuildingDetail = () => {
  const navigate = useNavigate();
  const { buildingId } = useParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');

  // 건물 상세 정보 가져오기
  const { data: buildingData, isLoading } = useQuery(
    ['rmc-building', buildingId],
    () => api.get(`/rmc/buildings/${buildingId}`)
  );

  // 건물 세대 목록 가져오기
  const { data: unitsData } = useQuery(
    ['rmc-building-units', buildingId],
    () => api.get(`/rmc/buildings/${buildingId}/units`)
  );

  // 건물 삭제 뮤테이션
  const deleteBuildingMutation = useMutation(
    () => api.delete(`/rmc/buildings/${buildingId}`),
    {
      onSuccess: () => {
        toast.success('건물이 성공적으로 삭제되었습니다.');
        queryClient.invalidateQueries('rmc-buildings');
        navigate('/rmc/buildings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '건물 삭제에 실패했습니다.');
      }
    }
  );

  // 건물 삭제 확인
  const handleDeleteBuilding = () => {
    if (window.confirm(`"${building?.name}" 건물을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) {
      deleteBuildingMutation.mutate();
    }
  };

  const building = buildingData?.building;
  const units = unitsData?.units || [];
  const statusLabel = building?.status === 'active'
    ? '활성'
    : building?.status === 'inactive'
      ? '비활성'
      : '유지보수';

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!building) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">건물 정보를 찾을 수 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* 헤더 */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <button
            onClick={() => navigate('/rmc/buildings')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            건물 목록으로 돌아가기
          </button>
          <h1 className="break-words text-xl font-bold text-gray-900 md:text-2xl">{building.name}</h1>
          <p className="mt-1 flex items-start text-gray-600">
            <MapPin className="mr-1 mt-0.5 h-4 w-4 shrink-0" />
            <span className="break-words">{building.address}</span>
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3">
          <button
            onClick={() => navigate(`/rmc/buildings/${buildingId}/edit`)}
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit className="w-4 h-4 mr-2" />
            수정
          </button>
          <button
            onClick={handleDeleteBuilding}
            className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            삭제
          </button>
        </div>
      </div>

      {/* 탭 메뉴 */}
      <div className="border-b border-gray-200 pb-2 md:pb-0">
        <nav className="grid grid-cols-2 gap-2 md:-mb-px md:flex md:gap-6 md:overflow-x-auto">
          {[
            { id: 'overview', name: '개요', icon: Eye },
            { id: 'facility', name: '시설 정보', icon: Settings },
            { id: 'condition', name: '상태 정보', icon: Eye },
            { id: 'units', name: '세대 관리', icon: Users },
            { id: 'management', name: '관리 정보', icon: Settings },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center rounded-md border px-2 py-2 text-sm font-medium md:justify-start md:rounded-none md:border-x-0 md:border-t-0 md:px-1 ${
                activeTab === tab.id
                  ? 'border-blue-500 bg-blue-50 text-blue-600 md:bg-transparent'
                  : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 md:border-transparent'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'overview' && (
        <div className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
            <SummaryCard icon={Building2} label="건물 유형" value={building.building_type || '미분류'} />
            <SummaryCard icon={Users} label="총 세대수" value={`${building.total_units || 0}세대`} tone="green" />
            <SummaryCard icon={Settings} label="관리 상태" value={statusLabel} tone="amber" />
            <SummaryCard icon={Calendar} label="건축년도" value={building.year_built ? `${building.year_built}년` : '-'} tone="slate" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
          {/* 기본 정보 */}
          <div className="space-y-4 lg:col-span-2 lg:space-y-6">
            <div className="bg-white rounded-lg shadow p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">기본 정보</h2>
              <dl className="grid grid-cols-2 gap-3 md:gap-4">
                <DetailField label="건물 유형" value={building.building_type || '미분류'} />
                <DetailField label="용도" value={building.building_purpose} />
                <DetailField label="총 층수" value={building.total_floors ? `${building.total_floors}층` : '-'} />
                <DetailField label="총 세대수" value={`${building.total_units || 0}세대`} />
                <DetailField label="연면적" value={building.total_floor_area ? `${building.total_floor_area}㎡` : '-'} />
                <DetailField label="구조" value={building.building_structure} />
                <DetailField label="엘리베이터" value={building.has_elevator ? '있음' : '없음'} />
                <DetailField label="건축년도" value={building.year_built ? `${building.year_built}년` : '-'} />
                <DetailField label="건물 코드" value={building.building_code} />
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 md:px-4">
                  <dt className="text-xs font-medium text-gray-500">상태</dt>
                  <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    building.status === 'active' 
                      ? 'bg-green-100 text-green-800'
                      : building.status === 'inactive'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {statusLabel}
                  </span>
                </div>
              </dl>
            </div>

            {/* 건물 상세 정보 */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">건물 상세 정보</h2>
              <dl className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-3">
                <DetailField label="대지면적" value={building.land_area ? `${building.land_area}㎡` : '-'} />
                <DetailField label="건물면적" value={building.building_area ? `${building.building_area}㎡` : '-'} />
                <DetailField label="용적률" value={building.floor_area_ratio ? `${building.floor_area_ratio}%` : '-'} />
                <DetailField label="건폐율" value={building.building_coverage_ratio ? `${building.building_coverage_ratio}%` : '-'} />
                <DetailField label="주차대수" value={`${building.parking_spaces || 0}대`} />
                <DetailField label="엘리베이터 수" value={`${building.elevator_count || 0}대`} />
              </dl>
            </div>
          </div>

          {/* 임대사업자 정보 */}
          <div className="space-y-4 lg:space-y-6">
            <div className="bg-white rounded-lg shadow p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">임대사업자 정보</h2>
              {building.property_owner ? (
                <dl className="grid grid-cols-2 gap-3">
                  <DetailField label="사업자명" value={building.property_owner.name} />
                  {building.property_owner.company_name && (
                    <DetailField label="회사명" value={building.property_owner.company_name} />
                  )}
                  {building.property_owner.email && (
                    <DetailField label="이메일" value={building.property_owner.email} />
                  )}
                  {building.property_owner.phone && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 md:px-4">
                      <dt className="text-xs font-medium text-gray-500">연락처</dt>
                      <dd className="mt-1 flex items-center text-sm font-semibold text-gray-900">
                        <Phone className="w-3 h-3 mr-1" />
                        {building.property_owner.phone}
                      </dd>
                    </div>
                  )}
                  {building.property_owner.business_number && (
                    <DetailField label="사업자등록번호" value={building.property_owner.business_number} />
                  )}
                  {building.property_owner.address && (
                    <DetailField label="주소" value={building.property_owner.address} className="col-span-2" />
                  )}
                </dl>
              ) : (
                <p className="text-gray-500">임대사업자 정보가 없습니다.</p>
              )}
            </div>

            {/* 등록 정보 */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">등록 정보</h2>
              <dl className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 md:px-4">
                  <dt className="text-xs font-medium text-gray-500">등록일</dt>
                  <dd className="mt-1 flex items-center text-sm font-semibold text-gray-900">
                    <Calendar className="w-3 h-3 mr-1" />
                    {building.created_at ? new Date(building.created_at).toLocaleDateString() : '-'}
                  </dd>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 md:px-4">
                  <dt className="text-xs font-medium text-gray-500">수정일</dt>
                  <dd className="mt-1 flex items-center text-sm font-semibold text-gray-900">
                    <Calendar className="w-3 h-3 mr-1" />
                    {building.updated_at ? new Date(building.updated_at).toLocaleDateString() : '-'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* 시설 정보 탭 */}
      {activeTab === 'facility' && (
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">시설 정보</h2>
          <dl className="grid grid-cols-2 gap-3 md:gap-4">
            <DetailField label="전기용량" value={building.electric_capacity ? `${building.electric_capacity}kW` : '-'} />
            <DetailField label="수도계량기 수" value={building.water_meter_count ? `${building.water_meter_count}개` : '-'} />
            <DetailField label="가스방식" value={building.gas_type} />
            <DetailField label="난방형태" value={building.heating_type} />
            <DetailField label="CCTV 유무" value={building.has_cctv ? '있음' : '없음'} />
          </dl>
        </div>
      )}

      {/* 상태 정보 탭 */}
      {activeTab === 'condition' && (
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">건물 상태 정보</h2>
          <dl className="grid grid-cols-2 gap-3 md:gap-4">
            <DetailField label="외벽 균열 상태" value={building.exterior_crack_status} />
            <DetailField label="도장상태" value={building.paint_status} />
            <DetailField label="옥상 방수상태" value={building.roof_waterproof_status} />
            <DetailField label="급수/배수 상태" value={building.water_supply_status} />
            <DetailField label="보일러 상태" value={building.boiler_status} />
            <DetailField label="소방시설 상태" value={building.fire_safety_status} />
            <DetailField label="전기배선 상태" value={building.electrical_wiring_status} />
            <DetailField label="복도 상태" value={building.corridor_status} />
            <DetailField label="주차장 상태" value={building.parking_status} />
            <DetailField label="조명 상태" value={building.lighting_status} />
            <DetailField label="청소상태" value={building.cleaning_status} />
          </dl>
        </div>
      )}

      {activeTab === 'units' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">세대 목록</h2>
            <button
              onClick={() => navigate(`/rmc/buildings/${buildingId}/units`)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Users className="w-4 h-4 mr-2" />
              세대 관리
            </button>
          </div>
          <div className="p-6">
            {units.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">등록된 세대가 없습니다.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {units.slice(0, 6).map((unit) => (
                  <div key={unit.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-gray-900">{unit.unit_number}</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        unit.status === 'occupied' 
                          ? 'bg-green-100 text-green-800'
                          : unit.status === 'available'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {unit.status === 'occupied' ? '임대중' : 
                         unit.status === 'available' ? '임대가능' : unit.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>{unit.floor}층 • {unit.area}㎡</p>
                      <p>{unit.unit_type || '원룸'}</p>
                      {unit.standard_monthly_rent && (
                        <p className="font-medium text-gray-900">
                          {unit.standard_monthly_rent.toLocaleString()}원
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {units.length > 6 && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate(`/rmc/buildings/${buildingId}/units`)}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  전체 {units.length}개 세대 보기 →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'management' && (
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">관리 정보</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">관리회사</h3>
              <dl className="grid grid-cols-2 gap-3">
                <DetailField label="관리회사명" value={building.management_company} />
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 md:px-4">
                  <dt className="text-xs font-medium text-gray-500">관리회사 연락처</dt>
                  <dd className="mt-1 flex items-center text-sm font-semibold text-gray-900">
                    <Phone className="w-3 h-3 mr-1" />
                    {building.management_phone || '-'}
                  </dd>
                </div>
                <DetailField label="월 관리비" value={building.monthly_maintenance_fee ? `${building.monthly_maintenance_fee.toLocaleString()}원` : '-'} />
              </dl>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">보안 및 시설</h3>
              <dl className="grid grid-cols-2 gap-3">
                <DetailField label="경비회사" value={building.security_company} />
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 md:px-4">
                  <dt className="text-xs font-medium text-gray-500">경비회사 연락처</dt>
                  <dd className="mt-1 flex items-center text-sm font-semibold text-gray-900">
                    <Phone className="w-3 h-3 mr-1" />
                    {building.security_phone || '-'}
                  </dd>
                </div>
                <DetailField label="난방 방식" value={building.heating_type} />
                <DetailField label="난방비" value={building.heating_fee ? `${building.heating_fee.toLocaleString()}원` : '-'} />
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RMCBuildingDetail;
